class ShopifyLeasePopup {
  constructor() {
    this.allProducts = [];
    this.variantMap = {}; // variantId → variant
    this.popup = null;
    this.pendingForm = null;
    this.pendingVariantId = null;
    this.agreementOpened = false;

    this.init();
  }

  async init() {
    this.addStyles();
    await this.fetchAllProducts();
    this.buildVariantMap();
    this.interceptAddToCart();
  }

  /* ----------------------------------
     PRODUCT DATA
  ---------------------------------- */
  async fetchAllProducts() {
    if (this.allProducts.length) return;

    try {
      const res = await fetch('/products.json?limit=250');
      const data = await res.json();
      this.allProducts = data.products || [];
    } catch (e) {
      console.error('Failed to load products', e);
    }
  }

  buildVariantMap() {
    this.allProducts.forEach(product => {
      product.variants.forEach(variant => {
        this.variantMap[String(variant.id)] = {
          title: variant.title,
          productTitle: product.title,
          productType: product.product_type.toLowerCase(),
          productHandle: product.handle
        };
      });
    });
  }

  isLeaseVariant(variantId) {
    const variant = this.variantMap[String(variantId)];
    if (!variant) return false;

    return variant.productType.toLowerCase() === 'leasing';
  }

  async hasBuyOrLeaseInCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();

      for (const item of cart.items) {
        const variant = this.variantMap[String(item.variant_id)];

        // Safety: unknown variant → treat as BUY
        if (!variant) {
          return { hasBuy: true, hasLease: false };
        }

        if (variant.productType.toLowerCase() === 'leasing') {
          return { hasBuy: false, hasLease: true };
        }

        // Any non-lease variant is BUY
        return { hasBuy: true, hasLease: false };
      }

      return { hasBuy: false, hasLease: false };
    } catch (e) {
      console.error('Failed to check cart', e);
      return { hasBuy: false, hasLease: false };
    }
  }


  /* ----------------------------------
     ADD TO CART INTERCEPTION
  ---------------------------------- */
  interceptAddToCart() {
    document.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!form.matches('form[action="/cart/add"]')) return;

        const variantId = form.querySelector('[name="id"]')?.value;
        if (!variantId) return;

        if (this.isLeaseVariant(variantId)) {
          e.preventDefault();
          e.stopImmediatePropagation();

          (async () => {
            const { hasBuy, hasLease } = await this.hasBuyOrLeaseInCart();

            if (hasBuy) {
              alert(
                "You already have a Buy product in your cart. Lease products must be purchased in a separate order."
              );
              return;
            }

            if (hasLease) {
              alert(
                "Only one lease product can be purchased per order."
              );
              return;
            }

            this.pendingForm = form;
            this.pendingVariantId = variantId;
            this.agreementOpened = false;
            this.showPopup();
          })();

        }
      },
      true // CAPTURE PHASE
    );
  }

  /* ----------------------------------
     POPUP UI
  ---------------------------------- */
  addStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .lease-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,.55);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }

      .lease-popup {
        background: white;
        border-radius: 12px;
        max-width: 780px;
        width: 100%;
        box-shadow: 0 20px 60px rgba(0,0,0,.35);
        animation: slideUp .25s ease-out;
      }

      .lease-header {
        padding: 24px 32px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .lease-header h2 {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
      }

      .lease-close {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #6b7280;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: color 0.2s;
      }

      .lease-close:hover {
        color: #374151;
      }

      .lease-content {
        padding: 15px 32px 0;
        max-height: 60vh;
        overflow-y: auto;
        font-size: 14px;
        line-height: 1.7;
        color: #374151;
      }

      .lease-footer {
        padding: 20px 32px;
        border-top: 1px solid #e5e7eb;
        background: #f9fafb;
      }

      .lease-accept {
        display: none;
        gap: 12px;
        align-items: center;
        margin-bottom: 16px;
      }

      .lease-accept.visible {
        display: flex;
      }

      .lease-checkbox {
        width: 22px;
        height: 22px;
        border: 2px solid #d1d5db;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .lease-checkbox.checked {
        background: #06b6d4;
        border-color: #06b6d4;
        color: white;
      }

      .lease-checkmark {
        font-size: 14px;
        font-weight: 700;
        display: none;
        line-height: 1;
      }

      .lease-checkbox.checked .lease-checkmark {
        display: block;
      }

      .lease-btn {
        padding: 12px 28px;
        border-radius: 8px;
        border: none;
        color: white;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .lease-btn.view-agreement {
        background: #06b6d4;
      }

      .lease-btn.view-agreement:hover {
        background: #0891b2;
      }

      .lease-btn.accept {
        background: #06b6d4;
        opacity: .5;
        pointer-events: none;
        display: none;
      }

      .lease-btn.accept.visible {
        display: inline-block;
      }

      .lease-btn.accept.enabled {
        opacity: 1;
        pointer-events: auto;
      }

      .lease-btn.accept.enabled:hover {
        background: #0891b2;
      }

      @keyframes slideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }

  showPopup() {
    if (this.popup) return;

    const productMetaData = JSON.parse(
      document.getElementById("product-meta-json").value
    );

    const tags = productMetaData.tags || [];

    // case-insensitive check
    const isResidential = tags.some(tag =>
      tag.toLowerCase().includes("residential")
    );

    const pdfUrl = isResidential
      ? "https://shopify-app.liquidoflife.net/public/residential-leasing-agreement-confirmation.pdf"
      : "https://shopify-app.liquidoflife.net/public/corporate-leasing-agreement-confirmation.pdf";

    const overlay = document.createElement('div');
    overlay.className = 'lease-overlay';
    overlay.innerHTML = `
      <div class="lease-popup">
        <div class="lease-header">
            <h2>Lease Agreement</h2>
            <button class="lease-close" aria-label="Close">&times;</button>
        </div>
        <div class="lease-content">
          <p><strong>${this.variantMap[this.pendingVariantId].productTitle}</strong></p>
          <p>This product is provided under a lease agreement.</p>
          <p>Please review the lease terms before continuing.</p>
        </div>
        <div class="lease-footer">
          <button class="lease-btn view-agreement" data-pdf-url="${pdfUrl}">
            View Agreement
          </button>
          <div class="lease-accept">
            <div class="lease-checkbox">
                <span class="lease-checkmark">✓</span>
            </div>
            <span>I agree to the lease terms</span>
          </div>
          <button class="lease-btn accept">Accept & Continue</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.popup = overlay;

    this.bindPopupEvents();
  }

  bindPopupEvents() {
    const viewBtn = this.popup.querySelector('.view-agreement');
    const acceptSection = this.popup.querySelector('.lease-accept');
    const checkbox = this.popup.querySelector('.lease-checkbox');
    const acceptBtn = this.popup.querySelector('.lease-btn.accept');
    const closeBtn = this.popup.querySelector('.lease-close');

    // View Agreement button
    viewBtn.addEventListener('click', () => {
      const pdfUrl = viewBtn.dataset.pdfUrl;
      window.open(pdfUrl, '_blank');

      // Show checkbox and accept button
      this.agreementOpened = true;
      viewBtn.style.display = 'none';
      acceptSection.classList.add('visible');
      acceptBtn.classList.add('visible');
    });

    // Toggle accept checkbox
    checkbox.addEventListener('click', () => {
      checkbox.classList.toggle('checked');
      acceptBtn.classList.toggle('enabled');
    });

    // Accept & continue
    acceptBtn.addEventListener('click', () => {
      if (!acceptBtn.classList.contains('enabled')) return;
      this.resumeAddToCart();
      this.closePopup();
    });

    // Close button
    closeBtn.addEventListener('click', () => {
      this.closePopup();
    });

    // Click outside popup
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) {
        this.closePopup();
      }
    });

    // ESC key
    this._escHandler = (e) => {
      if (e.key === 'Escape') {
        this.closePopup();
      }
    };
    document.addEventListener('keydown', this._escHandler);
  }

  closePopup() {
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }

    this.popup?.remove();
    this.popup = null;
    this.pendingForm = null;
    this.pendingVariantId = null;
    this.agreementOpened = false;
  }

  /* ----------------------------------
     RESUME ADD TO CART
  ---------------------------------- */
  resumeAddToCart() {
    fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(this.pendingForm),
      headers: { Accept: 'application/json' }
    }).then(() => {
      window.location.href = '/cart';
    });

    this.pendingForm = null;
    this.pendingVariantId = null;
  }
}

window.leasePopup = new ShopifyLeasePopup();